import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { ChatMessage, QuestionUsage } from './chat.types';

/** The last 10 exchanges are kept (in-memory + sessionStorage, per signed-in user). */
const MAX_STORED_MESSAGES = 20;

interface AssistantState {
  open: boolean;
  ownerId: string | null;
  messages: ChatMessage[];
  usage: QuestionUsage | null;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  setMessages: (updater: (messages: ChatMessage[]) => ChatMessage[]) => void;
  setUsage: (usage: QuestionUsage) => void;
  /** Starts a clean conversation for `ownerId` (also used on user switch / logout). */
  reset: (ownerId?: string | null) => void;
}

export const useAssistantStore = create<AssistantState>()(
  persist(
    (set) => ({
      open: false,
      ownerId: null,
      messages: [],
      usage: null,
      setOpen: (open) => set({ open }),
      toggle: () => set((s) => ({ open: !s.open })),
      setMessages: (updater) => set((s) => ({ messages: updater(s.messages).slice(-MAX_STORED_MESSAGES) })),
      setUsage: (usage) => set({ usage }),
      reset: (ownerId) => set((s) => ({ messages: [], usage: null, ownerId: ownerId === undefined ? s.ownerId : ownerId })),
    }),
    {
      name: 'nexops.ai.chat',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ ownerId: s.ownerId, messages: s.messages, usage: s.usage }),
      // A reload mid-stream leaves a half answer behind: mark it as stopped instead of streaming forever.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AssistantState>;
        return {
          ...current,
          ownerId: p.ownerId ?? null,
          usage: p.usage ?? null,
          messages: (p.messages ?? []).map((m) => (m.status === 'streaming' ? { ...m, status: 'aborted' as const } : m)),
        };
      },
    },
  ),
);
